// src/utils/openai.ts

import { supabase } from './supabase';          // ★プロジェクト既存のクライアント
import { buildTitleCategoryHint, cleanTitle } from './openai/titleUtils';
import { handleOpenAIError } from './openai/errors';
import {
  CATEGORIES_LOAD_PROMISE,
  resolveCategoryFromAiHints
} from './categories';

// Edge Function 名（supabase functions deploy <name> でデプロイ済み前提）
const FUNCTION_NAME = 'generate-title';
const TITLE_AND_CATEGORY_FUNCTION = 'generate-title-and-category';

export interface TitleAndCategoryResult {
  title: string;
  category: string;
  categoryYahoo: string;
  categoryResolved: boolean;
}

async function invokeEdgeFunction<T>(functionName: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, { body });

  if (error) {
    console.error(`Supabase Edge Function error (${functionName}):`, error);
    throw error;
  }

  if (!data) {
    throw new Error('Edge Function からデータが返されませんでした');
  }

  if (data.error) {
    throw new Error(data.error);
  }

  return data as T;
}

function handleEdgeConnectionError(err: unknown): never {
  const message = err instanceof Error ? err.message : String(err);
  if (
    err instanceof TypeError ||
    message.includes('Failed to fetch') ||
    message.includes('ERR_CONNECTION_REFUSED') ||
    message.includes('Failed to send a request to the Edge Function')
  ) {
    throw new Error(
      'Edge Function に接続できません。Supabase URL と ANON KEY が正しく設定されているか確認してください。',
    );
  }
  throw handleOpenAIError(err);
}

/**
 * 画像 URL 配列を渡して AI が生成したタイトル文字列を返す
 * @param images 商品画像 URL の配列
 * @param userId ユーザーID（クレジット消費のため）
 * @returns 整形済みタイトル
 */
export async function generateTitleFromImages(
  images: string[],
  userId: string,
  category: string
): Promise<string> {
  try {
    // 1) 引数チェック --------------------------------------------------------
    if (!images?.length || !userId) {
      throw new Error('画像またはユーザーIDが指定されていません');
    }

    images = images.slice(0, 3); // 最大3枚まで

    const data = await invokeEdgeFunction<{ title: string }>(FUNCTION_NAME, {
      images,
      userId,
      category: buildTitleCategoryHint(category)
    });

    if (!data.title) {
      throw new Error('タイトルを生成できませんでした');
    }

    console.log('Title generated successfully:', data.title);

    return cleanTitle(data.title);
  } catch (err: unknown) {
    console.error('Error in generateTitleFromImages:', err);
    handleEdgeConnectionError(err);
  }
}

/**
 * 画像からタイトルとカテゴリを同時に生成する
 */
export async function generateTitleAndCategoryFromImages(
  images: string[],
  userId: string,
  category: string
): Promise<TitleAndCategoryResult> {
  try {
    if (!images?.length || !userId) {
      throw new Error('画像またはユーザーIDが指定されていません');
    }

    const trimmedImages = images.slice(0, 3);

    const data = await invokeEdgeFunction<{
      title: string;
      categoryKeywords?: string[];
      categoryPathHint?: string;
    }>(TITLE_AND_CATEGORY_FUNCTION, {
      images: trimmedImages,
      userId,
      category: buildTitleCategoryHint(category)
    });

    if (!data.title) {
      throw new Error('タイトルを生成できませんでした');
    }

    await CATEGORIES_LOAD_PROMISE;

    const title = cleanTitle(data.title);
    const keywords = Array.isArray(data.categoryKeywords) ? data.categoryKeywords : [];
    const matched = resolveCategoryFromAiHints(keywords, data.categoryPathHint, title);

    return {
      title,
      category: matched?.path ?? '',
      categoryYahoo: matched ? String(matched.value) : '',
      categoryResolved: matched !== null
    };
  } catch (err: unknown) {
    console.error('Error in generateTitleAndCategoryFromImages:', err);
    handleEdgeConnectionError(err);
  }
}